<script setup lang="ts">
import { CenteredModal, floorDate, type Disclosure, useDateString } from '#shared'
import { ref, watch } from 'vue'
import ScheduleModalCalendar from './schedule-modal-calendar.vue'
import ScheduleModalTimeInput from './schedule-modal-time-input.vue'

const { disclosure } = defineProps<{ disclosure: Disclosure }>()

const emit = defineEmits<{ schedule: [date: Date] }>()

const scheduledAt = ref<Date | null>(null)

watch(
  () => disclosure.isOpen,
  isOpen => {
    if (isOpen) {
      scheduledAt.value = floorDate(new Date(), { unit: 'minute' })
    } else {
      scheduledAt.value = null
    }
  }
)

const dateString = useDateString(scheduledAt)
const isCalendarShown = ref(false)
const onDateClick = () => (isCalendarShown.value = true)
const onCalendarClose = () => (isCalendarShown.value = false)

const onDateSelect = (date: Date) => (scheduledAt.value = date)
const onScheduleClick = () => {
  disclosure.close()
  emit('schedule', scheduledAt.value!)
}
</script>

<template>
  <CenteredModal :modalClass="$style.root" :isOpen="disclosure.isOpen" @close="disclosure.close">
    <ScheduleModalCalendar
      v-if="isCalendarShown"
      :scheduledAt="scheduledAt"
      @date-select="onDateSelect"
      @close="onCalendarClose"
    />

    <div v-else :class="$style.content">
      <div :class="$style.title">Send this message on...</div>

      <div :class="$style.inputWrap">
        <button :class="$style.date" @click="onDateClick">
          {{ dateString }}
        </button>

        <div :class="$style.at">at</div>

        <ScheduleModalTimeInput :class="$style.time" v-model="scheduledAt" />
      </div>

      <div :class="$style.actions">
        <button :class="$style.cancel" @click="disclosure.close">Cancel</button>
        <button :class="$style.schedule" @click="onScheduleClick">Schedule</button>
      </div>
    </div>
  </CenteredModal>
</template>

<style lang="scss" module>
@use '#mixins/input' as input;
@use '#mixins/button' as button;

.root {
  width: calc(100vw - 40px);
  max-width: 400px;
}

.content {
  padding: 30px;
}

.title {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 32px;
}

.inputWrap {
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
}

.date {
  @include input.underline;

  & {
    padding-left: 10px;
    padding-right: 10px;
    white-space: nowrap;
    flex-grow: 1;
  }
}

.at {
  font-size: 14px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.time {
  width: 84px;
}

.actions {
  display: flex;
  gap: 16px;
}

.cancel {
  @include button.text;

  & {
    width: 100%;
    flex-grow: 1;
    box-sizing: border-box;
    text-align: center;
  }
}

.schedule {
  @include button.secondary;

  & {
    width: 100%;
    box-sizing: border-box;
    flex-grow: 1;
    text-align: center;
  }
}
</style>
